import { createApolloClient } from 'core/services/apollo/createApolloClient';
import {
  InitializeSourcedCandidatesCampaignDocument,
  Tone,
  UpdateCandidateBatchCustomJobPostingUrlDocument
} from 'generated-graphql/graphql';
import {
  SourcingSharedSetupApplicationFormData,
  SourcingSharedCustomizeMessageEmailFormData,
  SourcingSharedCustomizeMessageSmsFormData,
  SourcingSharedChooseChannelFormData
} from 'modules/sourcing-shared/forms';
import { type CampaignCreationSharedJobInformationFormData } from 'modules/campaign-creation-shared/forms';
import {
  SourcingSharedCandidateBatch,
  SourcingInviteSubmitPayload
} from 'modules/sourcing-shared/shared-stores/types';

type InitializeSourcingSharedControllerProps = {
  candidateBatch: SourcingSharedCandidateBatch;
  payload: SourcingInviteSubmitPayload;
  customizeMessageFormData:
    | SourcingSharedCustomizeMessageEmailFormData
    | SourcingSharedCustomizeMessageSmsFormData;
  jobInformationFormData: CampaignCreationSharedJobInformationFormData;
  channelFormData: SourcingSharedChooseChannelFormData;
  applicationFormData: SourcingSharedSetupApplicationFormData;
};

export const initializeSourcingSharedController = async ({
  candidateBatch,
  payload,
  customizeMessageFormData,
  jobInformationFormData,
  channelFormData,
  applicationFormData
}: InitializeSourcingSharedControllerProps) => {
  const client = createApolloClient();

  if (
    !applicationFormData ||
    !customizeMessageFormData ||
    !jobInformationFormData ||
    !channelFormData
  ) {
    return;
  }

  if (applicationFormData.customJobPostingUrl) {
    await client.mutate({
      mutation: UpdateCandidateBatchCustomJobPostingUrlDocument,
      variables: {
        input: {
          id: candidateBatch.id,
          customJobPostingUrl: applicationFormData.customJobPostingUrl
        }
      }
    });
  }

  await client.mutate({
    mutation: InitializeSourcedCandidatesCampaignDocument,
    variables: {
      input: {
        campaignType: applicationFormData.campaignType,
        customTemplatedSubjectLine:
          'customTemplatedSubjectLine' in customizeMessageFormData
            ? customizeMessageFormData.customTemplatedSubjectLine
            : '',
        customTemplatedMessage: customizeMessageFormData.customTemplatedMessage,
        candidateBatchId: candidateBatch.id,
        agentName: customizeMessageFormData.agent.name,
        searchCandidateProfileSelectedId:
          payload.type === 'internal'
            ? payload.searchCandidateProfileSelectedId
            : undefined,
        sourcedCandidatesIds:
          payload.type === 'external'
            ? payload.sourcedCandidatesIds
            : undefined,
        agentTone: Tone.Neutral,
        jobTitle: jobInformationFormData.jobTitle,
        employerName: jobInformationFormData.employerName,
        location: jobInformationFormData.location,
        contractType: jobInformationFormData.contractType || null,
        additionalContext: jobInformationFormData.additionalContext,
        channel: channelFormData.channel || undefined
      }
    }
  });
};
